import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { OtpStep } from './OtpStep';
import { PasswordStep } from './PasswordStep';
import { BrandLogo } from '@/shared/ui/BrandLogo';
import { useAuth } from '@/app/providers/AuthProvider';
import {
  buildScopedPath,
  isLikelyGuid,
  parseScopeFromPath,
  publicRoutes,
} from '@/app/routes';
import type { LoginResponseLike } from '@/shared/auth/storage';

type Tab = 'sms' | 'password';

export function LoginPage() {
  const [tab, setTab] = useState<Tab>('sms');
  const { loginFromResponse } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = (loc.state as { from?: string } | null)?.from ?? null;

  function handle(res: LoginResponseLike) {
    loginFromResponse(res);
    // Хэрэглэгчийн default tenant + company-аас scoped URL барина.
    const tid = res.DefaultTenantId ?? null;
    const cid = res.DefaultCompanyId ?? null;
    if (isLikelyGuid(tid) && isLikelyGuid(cid)) {
      // Хэрэв original URL нь scoped байсан бол түүн рүү буцаах нь зөв
      // (deep-link return). Зөвхөн `from` нь login биш, scoped URL бол.
      const fromScope = from ? parseScopeFromPath(from) : null;
      if (from && fromScope && fromScope.tenantId === tid && fromScope.companyId === cid) {
        nav(from, { replace: true });
        return;
      }
      nav(buildScopedPath('dashboard', { tenantId: tid, companyId: cid }), {
        replace: true,
      });
      return;
    }
    // Default scope байхгүй бол сонгуулна.
    nav(publicRoutes.selectScope, { replace: true });
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background:
          'linear-gradient(135deg, #0f1117 0%, #1a2a4a 60%, #0f1117 100%)',
      }}
    >
      <div style={{ width: 420, maxWidth: '92vw' }}>
        <Link
          to={publicRoutes.landing}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'rgba(255,255,255,0.6)',
            fontSize: '0.85rem',
            textDecoration: 'none',
            marginBottom: 12,
            padding: '6px 10px',
            borderRadius: 8,
            background: 'rgba(255,255,255,0.05)',
          }}
        >
          <i className="bi bi-arrow-left" /> Нүүр хуудас
        </Link>
      <div
        className="ts-card"
        style={{ padding: 28 }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <BrandLogo
            variant="full"
            size={180}
            className="mx-auto mb-3"
            fallback={
              <div
                className="ts-brand-icon"
                style={{
                  width: 72,
                  height: 72,
                  fontSize: '2rem',
                  borderRadius: 20,
                  margin: '0 auto 14px',
                }}
              >
                T
              </div>
            }
          />
          <h1 style={{ fontSize: '1.4rem', fontWeight: 800, margin: 0 }}>
            Tse<span style={{ color: 'var(--ts-accent)' }}>Tsu</span>
          </h1>
          <div style={{ color: 'var(--ts-muted)', fontSize: '0.85rem', marginTop: 6 }}>
            POS · Inventory · Sales
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 18, borderBottom: '1px solid var(--ts-border)' }}>
          <button
            type="button"
            onClick={() => setTab('sms')}
            style={{
              flex: 1,
              padding: '10px 0',
              border: 0,
              background: 'transparent',
              color: tab === 'sms' ? 'var(--bs-body-color)' : 'var(--ts-muted)',
              fontWeight: 600,
              borderBottom: `2px solid ${tab === 'sms' ? 'var(--ts-brand)' : 'transparent'}`,
              cursor: 'pointer',
            }}
          >
            <i className="bi bi-phone" /> SMS код
          </button>
          <button
            type="button"
            onClick={() => setTab('password')}
            style={{
              flex: 1,
              padding: '10px 0',
              border: 0,
              background: 'transparent',
              color: tab === 'password' ? 'var(--bs-body-color)' : 'var(--ts-muted)',
              fontWeight: 600,
              borderBottom: `2px solid ${tab === 'password' ? 'var(--ts-brand)' : 'transparent'}`,
              cursor: 'pointer',
            }}
          >
            <i className="bi bi-key" /> Нууц үг
          </button>
        </div>

        {tab === 'sms' ? <OtpStep onLoggedIn={handle} /> : <PasswordStep onLoggedIn={handle} />}
      </div>
      </div>
    </div>
  );
}
